import { useMemo } from 'react';
import { LatLngBoundsExpression, LatLngTuple } from 'leaflet';
import { MAP_CONFIG } from '../constants';

interface UseMapBoundsOptions {
  /**
   * Additional coordinates to include in bounds calculation (e.g., HQ location)
   */
  additionalCoords?: LatLngTuple[];
  /**
   * Padding to add for single coordinate (degrees)
   * Uses MAP_CONFIG.singleCoordPadding by default
   */
  singleCoordPadding?: number;
  /**
   * Fallback bounds when no coordinates are provided
   */
  fallbackBounds?: LatLngBoundsExpression;
}

/**
 * Calculate map bounds from workorder coordinates
 * Handles edge cases like single workorder and no workorders
 */
export function useMapBounds(
  workorderCoords: Map<number, LatLngTuple>,
  options: UseMapBoundsOptions = {},
): LatLngBoundsExpression {
  const {
    additionalCoords = [],
    singleCoordPadding = MAP_CONFIG.singleCoordPadding,
    fallbackBounds,
  } = options;

  return useMemo<LatLngBoundsExpression>(() => {
    const coords: LatLngTuple[] = [];

    // Collect workorder coordinates
    workorderCoords.forEach((coord) => {
      coords.push(coord);
    });

    // Add additional coordinates (e.g., HQ)
    coords.push(...additionalCoords);

    // No coordinates - use fallback or default
    if (coords.length === 0) {
      if (fallbackBounds) {
        return fallbackBounds;
      }
      // Default fallback to Netherlands center
      const [lat, lng] = MAP_CONFIG.defaultCenter;
      const padding = MAP_CONFIG.fallbackPadding;
      return [
        [lat - padding, lng - padding],
        [lat + padding, lng + padding],
      ];
    }

    // Single coordinate - add padding
    if (coords.length === 1) {
      const [lat, lng] = coords[0];
      return [
        [lat - singleCoordPadding, lng - singleCoordPadding],
        [lat + singleCoordPadding, lng + singleCoordPadding],
      ];
    }

    // Multiple coordinates - Leaflet will calculate bounds automatically
    return coords as LatLngBoundsExpression;
  }, [workorderCoords, additionalCoords, singleCoordPadding, fallbackBounds]);
}
